// ForgotPasswordPage — trang "Quên mật khẩu", dùng chung Core + platform + SaaS.
//
// Host responsibility (HOST_CONTRACT.md):
//   `@host/auth.useAuth()` phải có `forgotPassword(email) => Promise<void>`
//     - Core/platform: POST /api/auth/password/forgot
//     - SaaS:          POST /api/v1/auth/password/forgot
//
// ⚠️ Bảo mật: BE LUÔN trả 202 kể cả email không tồn tại (chống dò danh sách email).
// Nên trang này KHÔNG BAO GIỜ được nói "email không tồn tại" — gửi xong là hiện màn
// "đã gửi", hết. Lỗi hiện ra chỉ còn: mất mạng, 429 (xin quá nhiều), 5xx.
//
// ⚠️ i18n: cụm `pages-auth`. Chuỗi bọc `<Trans>` / `t`. Xem docs/I18N.md.

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Mail, Loader2, ArrowLeft, MailCheck, KeyRound } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useAuth } from '@host/auth';
import LoginShell from './LoginShell.jsx';
import '../../i18n/c/pages-auth.js';

const RESEND_COOLDOWN_SEC = 60;

/**
 * @param {object} p
 * @param {string} p.logoSrc / p.logoAlt / p.brandName / p.tagline - branding (giống LoginPage)
 * @param {string} p.variant - 'pink' (default) | 'blue'
 * @param {string} p.loginHref - đường về trang đăng nhập (default '/login')
 * @param {function} p.onLocaleChange
 * @param {string} p.initialEmail - điền sẵn ô email (optional). Không truyền thì đọc
 *   `location.state.email` (link "Quên mật khẩu?" của LoginPage, trang đăng ký Connect) — không
 *   đặt email lên URL.
 */
export default function ForgotPasswordPage({
  logoSrc,
  logoAlt,
  brandName,
  tagline,
  variant = 'pink',
  loginHref = '/login',
  onLocaleChange,
  initialEmail = '',
}) {
  const auth = useAuth();
  const { t } = useLingui();
  const location = useLocation();
  const [email, setEmail] = useState(() => initialEmail
    || (typeof location.state?.email === 'string' ? location.state.email : ''));
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  // Giữ email trong ref để timer/gửi lại không phải vào deps của effect.
  const lastEmailRef = useRef('');

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const send = async (addr) => {
    if (typeof auth?.forgotPassword !== 'function') {
      // Host chưa cắm method → nói thẳng, KHÔNG im lặng thành công giả.
      toast.error(t`Chức năng chưa được cài đặt trên hệ thống này`, { id: 'forgot-err' });
      return;
    }
    setSubmitting(true);
    try {
      await auth.forgotPassword(addr);
      lastEmailRef.current = addr;
      setSentTo(addr);
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      const status = err?.response?.status;
      // SaaS gói meta của RateLimit vào `error.details`; Core trả phẳng. Đọc cả hai.
      const errBody = err?.response?.data?.error;
      const retry = errBody?.details?.retry_after ?? errBody?.retry_after;
      if (status === 429) {
        toast.error(
          retry
            ? t`Bạn đã xin đặt lại quá nhiều lần. Thử lại sau ${Math.ceil(retry / 60)} phút.`
            : t`Bạn đã xin đặt lại quá nhiều lần. Vui lòng thử lại sau.`,
          { id: 'forgot-err' },
        );
      } else if (status >= 500) {
        toast.error(t`Hệ thống đang lỗi, thử lại sau`, { id: 'forgot-err' });
      } else if (err?.message?.includes('Network')) {
        toast.error(t`Mất kết nối, kiểm tra mạng`, { id: 'forgot-err' });
      } else {
        toast.error(t`Không gửi được email, vui lòng thử lại`, { id: 'forgot-err' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) {
      toast.error(t`Vui lòng nhập email`, { id: 'forgot-err' });
      return;
    }
    if (submitting) return;
    await send(addr);
  };

  const onResend = async () => {
    if (submitting || cooldown > 0) return;
    await send(lastEmailRef.current || email.trim());
  };

  const backToLogin = (
    <Link to={loginHref} className="inline-flex items-center gap-1.5 text-blue-600 font-medium hover:underline">
      <ArrowLeft size={14} />
      <Trans>Về trang đăng nhập</Trans>
    </Link>
  );

  // ── Đã gửi mail ────────────────────────────────────────────────────────────
  if (sentTo) {
    return (
      <LoginShell
        logoSrc={logoSrc}
        logoAlt={logoAlt}
        brandName={brandName}
        tagline={tagline}
        variant={variant}
        subtitle={t`Kiểm tra hộp thư`}
        subtitleHint=""
        onLocaleChange={onLocaleChange}
        footer={backToLogin}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
            <MailCheck size={26} className="text-emerald-600" />
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            <Trans>
              Nếu <span className="font-semibold text-slate-900">{sentTo}</span> là email của một
              tài khoản trên hệ thống, chúng tôi vừa gửi tới đó một liên kết đặt lại mật khẩu.
            </Trans>
          </p>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            <Trans>
              Liên kết hiệu lực 30 phút và chỉ dùng được một lần. Không thấy thư? Kiểm tra mục
              Spam / Quảng cáo.
            </Trans>
          </p>
          {/* Lối thoát cho người gõ nhầm email hoặc dùng email chưa được cấp tài khoản. Câu
              này hiện cho MỌI người, không phụ thuộc email có tồn tại hay không — nói riêng
              cho người "không có tài khoản" là biến màn này thành máy dò xem email nào đã
              đăng ký (xem chú thích ở route /password/forgot). */}
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">
            <Trans>
              Vẫn không nhận được? Có thể email này chưa được cấp tài khoản — liên hệ quản trị
              viên công ty bạn để được thêm vào hệ thống.
            </Trans>
          </p>

          <button
            type="button"
            onClick={onResend}
            disabled={submitting || cooldown > 0}
            className="mt-5 w-full rounded-lg border border-slate-300 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="mr-1.5 inline animate-spin" />}
            {cooldown > 0 ? (
              <Trans>Gửi lại sau {cooldown}s</Trans>
            ) : (
              <Trans>Gửi lại email</Trans>
            )}
          </button>

          <button
            type="button"
            onClick={() => { setSentTo(null); setCooldown(0); }}
            className="mt-2.5 text-xs text-slate-500 hover:text-slate-700 hover:underline"
          >
            <Trans>Dùng email khác</Trans>
          </button>
        </div>
      </LoginShell>
    );
  }

  // ── Nhập email ─────────────────────────────────────────────────────────────
  return (
    <LoginShell
      logoSrc={logoSrc}
      logoAlt={logoAlt}
      brandName={brandName}
      tagline={tagline}
      variant={variant}
      subtitle={t`Quên mật khẩu?`}
      subtitleHint={t`Nhập email của bạn, chúng tôi sẽ gửi liên kết để đặt lại mật khẩu.`}
      onLocaleChange={onLocaleChange}
      footer={backToLogin}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            <Trans>Email</Trans>
          </label>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t`ban@congty.com`}
              className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
          {submitting ? <Trans>Đang gửi...</Trans> : <Trans>Gửi liên kết đặt lại</Trans>}
        </button>
      </form>
    </LoginShell>
  );
}
